import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import UbicacionSelector from '../components/UbicacionSelector.jsx';
import { DCard, BtnGold, BtnGhost, SLabel } from '../components/MarketplaceAtoms.js';
import { PARTNER_ROLE_DEFS as ROLE_DEFS } from '../constants/partnerRoleDefs.js';
import { PARTNER_SCHEDULE_OPTIONS, roleNeedsPartnerSchedule } from '../constants/partnerSchedule.js';

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  error = '',
  status = 'neutral',
  compact = false,
  onBlur: onBlurProp,
}) {
  const borderColor =
    status === 'error'
      ? 'rgba(226,75,74,0.55)'
      : status === 'success'
        ? 'rgba(29,158,117,0.55)'
        : 'rgba(255,255,255,0.12)';
  return (
    <div style={{ marginBottom: compact ? 10 : 12 }}>
      {label ? <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div> : null}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 10,
          border: `0.5px solid ${borderColor}`,
          background: 'rgba(255,255,255,0.06)',
          color: '#fff',
          fontSize: 13,
          outline: 'none',
          fontFamily: 'inherit',
        }}
        onFocus={e => {
          e.target.style.borderColor =
            status === 'error' ? 'rgba(226,75,74,0.8)' : status === 'success' ? 'rgba(29,158,117,0.8)' : C.gold;
        }}
        onBlur={e => {
          e.target.style.borderColor = borderColor;
          onBlurProp?.(e);
        }}
      />
      {error ? <div style={{ marginTop: 6, fontSize: 10, color: C.danger }}>{error}</div> : null}
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label ? <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div> : null}
      <textarea
        rows={2}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 10,
          border: '0.5px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.06)',
          color: '#fff',
          fontSize: 13,
          outline: 'none',
          fontFamily: 'inherit',
          resize: 'vertical',
        }}
        onFocus={e => (e.target.style.borderColor = C.green)}
        onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, error = '', status = 'neutral' }) {
  const borderColor =
    status === 'error'
      ? 'rgba(226,75,74,0.55)'
      : status === 'success'
        ? 'rgba(29,158,117,0.55)'
        : 'rgba(255,255,255,0.12)';
  return (
    <div style={{ marginBottom: 12 }}>
      {label ? <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>{label}</div> : null}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 10,
          border: `0.5px solid ${borderColor}`,
          background: 'rgba(255,255,255,0.06)',
          color: '#fff',
          fontSize: 13,
          outline: 'none',
          fontFamily: 'inherit',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
        onFocus={e => {
          e.target.style.borderColor =
            status === 'error' ? 'rgba(226,75,74,0.8)' : status === 'success' ? 'rgba(29,158,117,0.8)' : C.gold;
        }}
        onBlur={e => {
          e.target.style.borderColor = borderColor;
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ color: '#111' }}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <div style={{ marginTop: 6, fontSize: 10, color: C.danger }}>{error}</div> : null}
    </div>
  );
}

function normalizeNifCif(raw) {
  return String(raw || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[-.]/g, '')
    .trim();
}

function isValidNIF(nifRaw) {
  const nif = normalizeNifCif(nifRaw);
  const m = nif.match(/^(\d{8})([A-Z])$/);
  if (!m) return false;
  const num = Number(m[1]);
  const letter = m[2];
  const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
  return letters[num % 23] === letter;
}

function isValidCIF(cifRaw) {
  const cif = normalizeNifCif(cifRaw);
  const m = cif.match(/^([A-Z])(\d{7})([0-9A-Z])$/);
  if (!m) return false;
  const type = m[1];
  const digits = m[2];
  const control = m[3];

  // CIF types (common set)
  if (!'ABCDEFGHJNPQRSUVW'.includes(type)) return false;

  let sumOdd = 0; // positions 1,3,5,7 (1-indexed within the 7 digits)
  let sumEven = 0; // positions 2,4,6
  for (let i = 0; i < digits.length; i++) {
    const d = Number(digits[i]);
    const pos = i + 1;
    if (pos % 2 === 0) {
      sumEven += d;
    } else {
      const x = d * 2;
      sumOdd += Math.floor(x / 10) + (x % 10);
    }
  }
  const total = sumEven + sumOdd;
  const controlDigit = (10 - (total % 10)) % 10;
  const controlLetter = 'JABCDEFGHI'[controlDigit];

  const mustBeLetter = 'PQSKW'.includes(type);
  const mustBeDigit = 'ABEH'.includes(type);

  if (mustBeLetter) return control === controlLetter;
  if (mustBeDigit) return control === String(controlDigit);
  return control === String(controlDigit) || control === controlLetter;
}

function isValidNifOrCif(raw) {
  return isValidNIF(raw) || isValidCIF(raw);
}

function isValidSpanishPhone(raw) {
  const digits = String(raw || '').replace(/[^\d+]/g, '');
  const onlyDigits = digits.replace(/\D/g, '');
  let n = onlyDigits;
  if (n.startsWith('34') && n.length === 11) n = n.slice(2);
  if (n.length !== 9) return false;
  return /^[6789]\d{8}$/.test(n);
}

function isValidEmail(raw) {
  const s = String(raw || '').trim();
  // simple & strict enough for UX; avoids allowing arbitrary text
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

const SECTOR_OPTIONS = [
  { value: '', label: 'Selecciona un sector…' },
  { value: 'tienda_discos', label: 'Disquería / comercio de discos' },
  { value: 'comercio_online', label: 'Comercio online' },
  { value: 'sello', label: 'Sello discográfico' },
  { value: 'festival', label: 'Festival' },
  { value: 'audio', label: 'Audio y equipamiento' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'streaming', label: 'Streaming' },
  { value: 'artista', label: 'Artista / Banda' },
  { value: 'venue', label: 'Espacio cultural' },
  { value: 'otro', label: 'Otro' },
];

const PARTNER_WELCOME_BENEFITS = [
  {
    icon: 'eye',
    title: 'Visibilidad activa',
    desc: 'Interactúa, participa y conoce a tu comunidad. Tu perfil Partner siempre visible.',
  },
  { icon: 'dollar', title: 'Ingresos extra', desc: 'Cobra por custodia y verificación.' },
  { icon: 'shield', title: 'Badge verificado', desc: 'Sello oficial de confianza DreamVinyl.' },
  { icon: 'map', title: 'En el mapa', desc: 'Aparece en el directorio de Partners.' },
  { icon: 'chat', title: 'Comunidad', desc: 'Publica eventos y conecta con tu audiencia.' },
  { icon: 'globe', title: 'Publicidad', desc: 'Campañas dirigidas a tu público exacto.' },
  { icon: 'calendar', title: 'Vende entradas', desc: 'Gestiona eventos y venta de entradas.' },
  {
    icon: 'disc',
    title: 'Grooves',
    desc: 'Interactúa, participa y acumula puntos. Canjéalos por descuentos, ventajas y sorteos exclusivos.',
  },
];

async function fetchPartnerRolesLabel(partnerId) {
  const map = new Map(ROLE_DEFS.map(r => [r.key, r.label]));
  const { data, error } = await supabase
    .from('partner_roles')
    .select('role')
    .eq('partner_id', partnerId)
    .eq('is_active', true);
  if (error || !data?.length) return '—';
  const labels = [...new Set(data.map(r => r.role))]
    .filter(k => map.has(k))
    .map(k => map.get(k));
  return labels.length ? labels.join(' · ') : '—';
}

export default function PagePartnerRegistro({ onBack, onNavigate, user, partnerOpsEnabled = true }) {
  const [bootstrapping, setBootstrapping] = useState(() => !!user?.id);
  const [existingPartner, setExistingPartner] = useState(null);
  const [forceShowFormAfterReject, setForceShowFormAfterReject] = useState(false);
  const [subPage, setSubPage] = useState('form'); // 'form' | 'estado'
  const [step, setStep] = useState(1); // 1: datos básicos, 2: documentos, 3: servicios
  const [attempted, setAttempted] = useState({ 1: false, 2: false, 3: false });
  const [scheduleType, setScheduleType] = useState('standard');

  // Controlled fields
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [nif, setNif] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [localidad, setLocalidad] = useState('');
  const [country, setCountry] = useState('España');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Step 2: documents / sector
  const [sector, setSector] = useState('');
  const [artisticName, setArtisticName] = useState('');

  // Step 3: roles + legal acceptance
  const [roles, setRoles] = useState(['store']);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [confirmBusiness, setConfirmBusiness] = useState(false);

  // Per-field "touched" (start with NIF/CIF only)
  const [touched, setTouched] = useState({ nif: false, contactEmail: false, contactPhone: false });

  // Step 2 document (UI only)
  const [docName, setDocName] = useState('');

  const [, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);

  const selectedRolesLabel = useMemo(() => {
    const map = new Map(ROLE_DEFS.map(r => [r.key, r.label]));
    const labels = roles
      .slice()
      .filter(k => map.has(k))
      .map(k => map.get(k));
    return labels.length ? labels.join(' · ') : '—';
  }, [roles]);

  const tabs = useMemo(() => ['Datos básicos', 'Documentos', 'Servicios'], []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapPartner() {
      if (!user?.id) {
        setBootstrapping(false);
        setExistingPartner(null);
        return;
      }

      setBootstrapping(true);
      setExistingPartner(null);

      const { data, error } = await supabase
        .from('partners')
        .select('id, verification_status, rejection_reason, company_name, nif, is_active')
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setBootstrapping(false);
        return;
      }

      setExistingPartner(data);

      if (data.verification_status === 'pending' || data.verification_status === 'reviewing') {
        const rolesLabel = await fetchPartnerRolesLabel(data.id);
        if (cancelled) return;
        setSubmittedSnapshot({
          companyName: data.company_name?.trim() || '—',
          nif: data.nif?.trim() || '—',
          rolesLabel,
        });
        setSubPage('estado');
      }

      setBootstrapping(false);
    }

    bootstrapPartner();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const validateStep1 = useCallback(() => {
    const errs = {};
    if (!companyName.trim()) errs.companyName = 'Campo obligatorio';
    if (!contactName.trim()) errs.contactName = 'Campo obligatorio';
    if (!nif.trim()) errs.nif = 'Campo obligatorio';
    if (!contactEmail.trim()) errs.contactEmail = 'Campo obligatorio';
    if (!contactPhone.trim()) errs.contactPhone = 'Campo obligatorio';
    if (!address.trim()) errs.address = 'Campo obligatorio';
    if (!city.trim()) errs.city = 'Campo obligatorio';

    if (nif.trim() && !isValidNifOrCif(nif)) {
      errs.nif = 'NIF/CIF no válido. Comprueba el formato.';
    }

    if (contactEmail.trim() && !isValidEmail(contactEmail)) {
      errs.contactEmail = 'Email no válido. Introduce un correo válido.';
    }

    if (contactPhone.trim() && !isValidSpanishPhone(contactPhone)) {
      errs.contactPhone = 'Teléfono no válido. Introduce un número español de 9 dígitos.';
    }

    return errs;
  }, [companyName, contactName, nif, contactEmail, contactPhone, address, city]);

  const validateStep2 = useCallback(() => {
    const errs = {};
    if (!sector) errs.sector = 'Selecciona un sector';
    if (sector === 'artista' && !artisticName.trim()) errs.artisticName = 'Campo obligatorio';
    if (!docName.trim()) errs.doc = 'Sube un documento para continuar.';
    return errs;
  }, [sector, artisticName, docName]);

  const validateStep3 = useCallback(() => {
    const errs = {};
    if (!roles.length) errs.roles = 'Selecciona al menos un rol';
    if (!acceptTerms) errs.acceptTerms = 'Debes aceptar los términos';
    if (!confirmBusiness) errs.confirmBusiness = 'Debes confirmar esta casilla';
    return errs;
  }, [roles, acceptTerms, confirmBusiness]);

  const nifTouched = !!touched.nif;
  const nifHasValue = !!nif.trim();
  const nifIsValid = nifHasValue && isValidNifOrCif(nif);
  const nifStatus = !nifTouched ? 'neutral' : nifIsValid ? 'success' : 'error';
  const nifTouchedError = !nifTouched ? '' : nifIsValid ? '' : 'NIF/CIF no válido. Comprueba el formato.';

  const emailTouched = !!touched.contactEmail;
  const emailHasValue = !!contactEmail.trim();
  const emailIsValid = emailHasValue && isValidEmail(contactEmail);
  const emailStatus = !emailTouched ? 'neutral' : emailIsValid ? 'success' : 'error';
  const emailTouchedError = !emailTouched ? '' : emailIsValid ? '' : 'Email no válido. Introduce un correo válido.';

  const phoneTouched = !!touched.contactPhone;
  const phoneHasValue = !!contactPhone.trim();
  const phoneIsValid = phoneHasValue && isValidSpanishPhone(contactPhone);
  const phoneStatus = !phoneTouched ? 'neutral' : phoneIsValid ? 'success' : 'error';
  const phoneTouchedError =
    !phoneTouched ? '' : phoneIsValid ? '' : 'Teléfono no válido. Introduce un número español de 9 dígitos.';

  const step1Errs = useMemo(() => (attempted[1] ? validateStep1() : {}), [attempted, validateStep1]);

  const step2Errs = useMemo(() => (attempted[2] ? validateStep2() : {}), [attempted, validateStep2]);

  const step3Errs = useMemo(() => (attempted[3] ? validateStep3() : {}), [attempted, validateStep3]);

  const canShowSchedulePicker = useMemo(() => roles.some(r => roleNeedsPartnerSchedule(r)), [roles]);

  const goToStep = nextStep => {
    // Allow going backwards freely, but guard forward transitions.
    if (nextStep <= step) {
      setFieldErrors({});
      setSubmitError('');
      setStep(nextStep);
      return;
    }
    if (step === 1) {
      setAttempted(a => ({ ...a, 1: true }));
      const errs = validateStep1();
      setFieldErrors(errs);
      if (Object.keys(errs).length) return;
    }
    if (step === 2) {
      setAttempted(a => ({ ...a, 2: true }));
      const errs = validateStep2();
      setFieldErrors(errs);
      if (Object.keys(errs).length) return;
    }
    setFieldErrors({});
    setSubmitError('');
    setStep(nextStep);
  };

  const toggleRole = key => {
    setRoles(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]));
  };

  const resetFormForNewRequest = () => {
    setForceShowFormAfterReject(true);
    setSubPage('form');
    setStep(1);
    setAttempted({ 1: false, 2: false, 3: false });
    setScheduleType('standard');
    setCompanyName('');
    setContactName('');
    setNif('');
    setContactEmail('');
    setContactPhone('');
    setAddress('');
    setCity('');
    setLocalidad('');
    setCountry('España');
    setDescription('');
    setWebsiteUrl('');
    setSector('');
    setArtisticName('');
    setRoles(['store']);
    setAcceptTerms(false);
    setConfirmBusiness(false);
    setTouched({ nif: false, contactEmail: false, contactPhone: false });
    setDocName('');
    setFieldErrors({});
    setSubmitError('');
    setSubmittedSnapshot(null);
  };

  const handleSubmit = async () => {
    setSubmitError('');
    setAttempted(a => ({ ...a, 1: true, 2: true, 3: true }));
    const errs1 = validateStep1();
    const errs2 = validateStep2();
    const errs3 = validateStep3();
    const errs = { ...errs1, ...errs2, ...errs3 };
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      const partnerPayload = {
        profile_id: user?.id ?? null,
        company_name: companyName.trim(),
        contact_name: contactName.trim(),
        nif: nif.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim(),
        address: address.trim(),
        city: city.trim(),
        localidad: localidad.trim() || null,
        country: String(country || '').trim() || 'España',
        description: description.trim() || null,
        website_url: websiteUrl.trim() || null,
        sector: sector || null,
        artist_name: sector === 'artista' ? artisticName.trim() : null,
        notes: null,
        verification_status: 'pending',
      };

      const { data: partnerRow, error: partnerErr } = await supabase
        .from('partners')
        .insert(partnerPayload)
        .select('id, code, company_name, nif, contact_email, contact_phone, sector, verification_status')
        .single();

      if (partnerErr || !partnerRow?.id) {
        throw new Error(partnerErr?.message || 'No se pudo guardar la solicitud.');
      }

      const partnerId = partnerRow.id;

      const uniqueRoles = [...new Set(roles)].filter(r => ROLE_DEFS.some(d => d.key === r));
      const rolesPayload = uniqueRoles.map(role => {
        const needsSchedule = roleNeedsPartnerSchedule(role);
        return {
          partner_id: partnerId,
          role,
          is_active: true,
          role_data: needsSchedule ? { schedule_type: scheduleType } : {},
        };
      });

      if (rolesPayload.length) {
        const { error: rolesErr } = await supabase.from('partner_roles').insert(rolesPayload);
        if (rolesErr) throw new Error(rolesErr.message || 'No se pudieron guardar los servicios.');
      }

      const { data: fnData, error: fnError } = await supabase.functions.invoke('partner-notification', {
        body: {
          id: partnerRow.id,
          code: partnerRow.code,
          company_name: partnerRow.company_name,
          nif: partnerRow.nif,
          contact_email: partnerRow.contact_email,
          contact_phone: partnerRow.contact_phone,
          sector: partnerRow.sector,
          verification_status: partnerRow.verification_status,
        },
      });
      if (fnError) console.error('Error invocando partner-notification:', fnError);
      else console.log('partner-notification response:', fnData);

      setSubmittedSnapshot({
        companyName: companyName.trim(),
        nif: nif.trim(),
        rolesLabel: selectedRolesLabel,
      });
      setSubPage('estado');
    } catch (e) {
      setSubmitError(e?.message || 'No se pudo enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (bootstrapping) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        </div>
      </div>
    );
  }

  if (existingPartner?.verification_status === 'approved' && existingPartner.is_active === false) {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 14px 60px' }}>
          <DCard style={{ padding: 18, background: 'rgba(239,159,39,0.08)', borderColor: 'rgba(239,159,39,0.2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 6 }}>Cuenta Partner desactivada</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', lineHeight: 1.5 }}>
              Tu verificación sigue vigente, pero la cuenta está desactivada en estos momentos. Escríbenos desde ayuda si necesitas aclaraciones.
            </div>
          </DCard>
        </div>
      </div>
    );
  }

  if (existingPartner?.verification_status === 'approved') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 14px 60px' }}>
          <DCard style={{ padding: 18, marginBottom: 18, background: 'rgba(29,158,117,0.08)', borderColor: 'rgba(29,158,117,0.2)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 6 }}>Tu cuenta Partner ya está activa</div>
            {!partnerOpsEnabled ? (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)', lineHeight: 1.55, marginTop: 8 }}>
                El panel operativo (custodia, puntos de recogida, logística) se habilitará en la siguiente fase del lanzamiento.
                Mientras tanto puedes seguir usando el mercado y la comunidad.
              </p>
            ) : null}
          </DCard>
          {partnerOpsEnabled ? (
            <BtnGold onClick={() => onNavigate('partner-panel')}>Ir al panel Partner</BtnGold>
          ) : null}
        </div>
      </div>
    );
  }

  if (existingPartner?.verification_status === 'rejected' && !forceShowFormAfterReject) {
    const reason =
      typeof existingPartner.rejection_reason === 'string' && existingPartner.rejection_reason.trim()
        ? existingPartner.rejection_reason.trim()
        : 'No se indicó motivo.';
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 14px 60px' }}>
          <DCard style={{ padding: 18, marginBottom: 18, background: 'rgba(226,75,74,0.08)', borderColor: 'rgba(226,75,74,0.22)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Solicitud no aprobada</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', lineHeight: 1.55 }}>{reason}</div>
          </DCard>
          <BtnGold onClick={resetFormForNewRequest}>Enviar nueva solicitud</BtnGold>
        </div>
      </div>
    );
  }

  if (existingPartner?.verification_status === 'suspended') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 14px 60px' }}>
          <DCard style={{ padding: 18, background: 'rgba(239,159,39,0.08)', borderColor: 'rgba(239,159,39,0.2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 6 }}>Cuenta Partner suspendida</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', lineHeight: 1.5 }}>
              Esta cuenta no está disponible temporalmente. Contacta con soporte si necesitas ayuda.
            </div>
          </DCard>
        </div>
      </div>
    );
  }

  if (subPage === 'estado') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        <WebTopBar
          onBack={onBack}
          title="Estado de verificación"
          mini
          dark
          user={user}
          onNavigate={onNavigate}
          onSignOut={onBack}
        />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 14px 60px' }}>
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(239,159,39,0.06)',
              borderRadius: 12,
              border: '0.5px solid rgba(239,159,39,0.15)',
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 500, color: C.warn, marginBottom: 3 }}>Solicitud en revisión</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
              El equipo DreamVinyl está revisando tu solicitud. Normalmente tarda entre 24 y 48 horas laborables.
            </div>
          </div>

          {/* Timeline verificación */}
          {[
            { icon: 'check', state: 'done', label: 'Datos recibidos', sub: 'Solicitud enviada correctamente' },
            { icon: 'calendar', state: 'act', label: 'Verificando NIF/CIF', sub: 'Comprobando con el Registro Mercantil' },
            { icon: 'file', state: 'pend', label: 'Revisión de documentación', sub: 'Pendiente' },
            { icon: 'check', state: 'pend', label: 'Cuenta activada', sub: 'Pendiente' },
          ].map((st, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1.5px solid ${
                      st.state === 'done' ? C.green : st.state === 'act' ? C.warn : 'rgba(255,255,255,0.12)'
                    }`,
                    background:
                      st.state === 'done'
                        ? 'rgba(29,158,117,0.2)'
                        : st.state === 'act'
                          ? 'rgba(239,159,39,0.15)'
                          : 'rgba(255,255,255,0.04)',
                  }}
                >
                  <Icon
                    name={st.icon}
                    size={12}
                    color={st.state === 'done' ? C.green : st.state === 'act' ? C.warn : 'rgba(255,255,255,0.2)'}
                  />
                </div>
                {i < 3 && <div className={`tl-line-conn${st.state === 'done' ? ' done' : ''}`} />}
              </div>
              <div style={{ paddingTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: st.state === 'pend' ? 'rgba(255,255,255,0.35)' : '#fff' }}>
                  {st.label}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: st.state === 'pend' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.35)',
                    marginTop: 2,
                  }}
                >
                  {st.sub}
                </div>
              </div>
            </div>
          ))}

          {/* Datos enviados */}
          <DCard style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: '#fff', marginBottom: 8 }}>Datos enviados</div>
            {[
              ['Nombre comercial', submittedSnapshot?.companyName || companyName.trim() || '—'],
              ['NIF/CIF', submittedSnapshot?.nif || nif.trim() || '—'],
              ['Servicios', submittedSnapshot?.rolesLabel || selectedRolesLabel || '—'],
              ['Estado', 'En revisión'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{l}</span>
                <span style={{ fontSize: 11, color: l === 'Estado' ? C.warn : '#fff' }}>{v}</span>
              </div>
            ))}
          </DCard>

          <DCard style={{ padding: '10px 14px', background: 'rgba(83,74,183,0.06)', borderColor: 'rgba(83,74,183,0.2)' }}>
            <p style={{ fontSize: 11, color: C.purpleL, lineHeight: 1.5 }}>
              Te notificaremos por email y en la app cuando la verificación esté completa. Si necesitamos información adicional te contactaremos directamente.
            </p>
          </DCard>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar onBack={onBack} title="Solicitud Registro — Mi Partner" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 0 60px' }}>
        {/* Welcome header — mismo lenguaje visual que bloque destacado en PageComunidad */}
        <div
          style={{
            margin: '16px 14px 0',
            borderRadius: 14,
            background: 'linear-gradient(135deg, #1a0d2e, #0d1a2e)',
            position: 'relative',
            overflow: 'hidden',
            padding: '20px 18px 18px',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse at 30% 55%, rgba(200,168,75,0.14) 0%, transparent 58%)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2
              style={{
                color: '#fff',
                fontSize: 'clamp(1.15rem, 4.2vw, 1.5rem)',
                fontWeight: 700,
                margin: '0 0 10px',
                fontFamily: 'Georgia, serif',
                lineHeight: 1.28,
              }}
            >
              <span style={{ display: 'block' }}>Pongamos la aguja sobre los mismos surcos.</span>
              <span style={{ display: 'block', marginTop: '0.35em' }}>
                Únete como Partner al coleccionismo DreamVinyl.
              </span>
            </h2>
            <p
              style={{
                margin: 0,
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                fontWeight: 400,
              }}
            >
              Disquerías, artistas, sellos, promotores, espacios culturales y más. Si tu mundo gira alrededor de la música física, este es tu lugar.
            </p>
          </div>
        </div>

        <div style={{ padding: '12px 14px 0' }}>
          {/* Beneficios */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
            {PARTNER_WELCOME_BENEFITS.map(({ icon: ic, title: t, desc: s }) => (
              <DCard
                key={t}
                style={{
                  padding: '8px 9px',
                  background: 'rgba(29,158,117,0.06)',
                  borderColor: 'rgba(29,158,117,0.14)',
                  display: 'flex',
                  gap: 7,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 5,
                    background: 'rgba(239,159,39,0.1)',
                    border: '0.5px solid rgba(239,159,39,0.14)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={ic} size={11} color={t === 'Grooves' ? C.gold : C.green} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#fff', marginBottom: 3, letterSpacing: '0.02em' }}>{t}</div>
                  <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.38)', lineHeight: 1.28 }}>{s}</div>
                </div>
              </DCard>
            ))}
          </div>

          {/* Progress */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              {tabs.map((s, i) => {
                const idx = i + 1;
                const active = idx === step;
                const done = idx < step;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => goToStep(idx)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: idx <= step ? 'pointer' : 'default',
                      flex: 1,
                      textAlign: 'center',
                      fontSize: 9,
                      color: active ? C.green : done ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.3)',
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.round((step / 3) * 100)}%`, background: C.green, borderRadius: 2 }} />
            </div>
          </div>

          {/* Step 1 — Datos básicos */}
          {step === 1 ? (
            <>
              <SLabel>Información del negocio</SLabel>
              <InputField
                label="Nombre del negocio *"
                placeholder="ej: DreamVinyl Partner"
                value={companyName}
                onChange={setCompanyName}
                error={step1Errs.companyName || ''}
                status={attempted[1] ? (step1Errs.companyName ? 'error' : companyName.trim() ? 'success' : 'neutral') : 'neutral'}
              />
              <InputField
                label="Nombre de contacto *"
                placeholder="Nombre y apellidos"
                value={contactName}
                onChange={setContactName}
                error={step1Errs.contactName || ''}
                status={
                  attempted[1] ? (step1Errs.contactName ? 'error' : contactName.trim() ? 'success' : 'neutral') : 'neutral'
                }
              />
              <InputField
                label="NIF / CIF *"
                placeholder="B12345678"
                value={nif}
                onChange={setNif}
                error={attempted[1] ? step1Errs.nif || '' : nifTouchedError}
                status={attempted[1] ? (step1Errs.nif ? 'error' : nif.trim() ? 'success' : 'neutral') : nifStatus}
                onBlur={() => setTouched(t => ({ ...t, nif: true }))}
              />
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: -8, marginBottom: 12 }}>
                Introduce tu NIF o CIF
              </div>
              <InputField
                label="Email de negocio *"
                placeholder="info@tupartner.com"
                type="email"
                value={contactEmail}
                onChange={setContactEmail}
                error={attempted[1] ? step1Errs.contactEmail || '' : emailTouchedError}
                status={attempted[1] ? (step1Errs.contactEmail ? 'error' : contactEmail.trim() ? 'success' : 'neutral') : emailStatus}
                onBlur={() => setTouched(t => ({ ...t, contactEmail: true }))}
              />
              <InputField
                label="Teléfono de negocio *"
                placeholder="+34 91 000 00 00"
                type="tel"
                value={contactPhone}
                onChange={setContactPhone}
                error={attempted[1] ? step1Errs.contactPhone || '' : phoneTouchedError}
                status={attempted[1] ? (step1Errs.contactPhone ? 'error' : contactPhone.trim() ? 'success' : 'neutral') : phoneStatus}
                onBlur={() => setTouched(t => ({ ...t, contactPhone: true }))}
              />
              <InputField
                label="Dirección completa *"
                placeholder="Calle, número, código postal"
                value={address}
                onChange={setAddress}
                error={step1Errs.address || ''}
                status={attempted[1] ? (step1Errs.address ? 'error' : address.trim() ? 'success' : 'neutral') : 'neutral'}
              />

              <InputField label="País" placeholder="España" value={country} onChange={setCountry} compact />

              {/* Ubicación (Provincia/Municipio) */}
              <div style={{ marginBottom: 12 }}>
                {(() => {
                  const status =
                    !attempted[1]
                      ? 'neutral'
                      : step1Errs.city
                        ? 'error'
                        : String(city || '').trim()
                          ? 'success'
                          : 'neutral';
                  const ring =
                    status === 'error'
                      ? 'rgba(226,75,74,0.55)'
                      : status === 'success'
                        ? 'rgba(29,158,117,0.55)'
                        : 'transparent';
                  return (
                    <div style={{ borderRadius: 12, boxShadow: ring === 'transparent' ? 'none' : `0 0 0 1px ${ring}` }}>
                      <UbicacionSelector
                        variant="dark"
                        soloEspana
                        required
                        provincia={city}
                        municipio={localidad}
                        onProvinciaChange={n => {
                          setCity(n);
                          setLocalidad('');
                        }}
                        onMunicipioChange={setLocalidad}
                      />
                    </div>
                  );
                })()}
                {step1Errs.city ? <div style={{ marginTop: 6, fontSize: 10, color: C.danger }}>{step1Errs.city}</div> : null}
              </div>

              <TextareaField
                label="Descripción"
                placeholder="Especialidad, años de experiencia, géneros..."
                value={description}
                onChange={setDescription}
              />
              <InputField label="Web" placeholder="https://tusitio.com" type="url" value={websiteUrl} onChange={setWebsiteUrl} />

              <button
                type="button"
                onClick={() => goToStep(2)}
                style={{
                  width: '100%',
                  padding: 14,
                  borderRadius: 12,
                  background: C.green,
                  color: '#0a0a0f',
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 4,
                }}
              >
                Siguiente →
              </button>
              <div style={{ marginTop: 8 }}>
                <BtnGhost>Guardar borrador</BtnGhost>
              </div>
            </>
          ) : null}

          {/* Step 2 — Documentos */}
          {step === 2 ? (
            <>
              <SLabel>Documentos</SLabel>
              <SelectField
                label="Sector *"
                value={sector}
                onChange={setSector}
                options={SECTOR_OPTIONS}
                error={step2Errs.sector || ''}
                status={attempted[2] ? (step2Errs.sector ? 'error' : sector ? 'success' : 'neutral') : 'neutral'}
              />

              {sector === 'artista' ? (
                <InputField
                  label="Nombre artístico / nombre del proyecto *"
                  placeholder="ej: Los Surcos"
                  value={artisticName}
                  onChange={setArtisticName}
                  error={step2Errs.artisticName || ''}
                  status={
                    attempted[2]
                      ? step2Errs.artisticName
                        ? 'error'
                        : artisticName.trim()
                          ? 'success'
                          : 'neutral'
                      : 'neutral'
                  }
                />
              ) : null}

              <div
                role="button"
                tabIndex={0}
                onClick={() => document.getElementById('partner-doc-upload')?.click()}
                onKeyDown={e => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  document.getElementById('partner-doc-upload')?.click();
                }}
                style={{ border: `1.5px dashed rgba(29,158,117,0.3)`, borderRadius: 12, padding: 16, textAlign: 'center', cursor: 'pointer', background: 'rgba(29,158,117,0.04)', marginBottom: 14 }}
              >
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  style={{ display: 'none' }}
                  id="partner-doc-upload"
                  onChange={e => {
                    const f = e.target.files?.[0] || null;
                    setDocName(f ? f.name : '');
                  }}
                />
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(29,158,117,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                  <Icon name="file" size={16} color={C.green} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#fff', marginBottom: 2 }}>Licencia de actividad o CIF</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                  {docName ? `Seleccionado: ${docName}` : 'PDF o imagen · Máximo 10MB'}
                </div>
              </div>
              {step2Errs.doc ? <div style={{ marginTop: -6, marginBottom: 12, fontSize: 10, color: C.danger }}>{step2Errs.doc}</div> : null}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  style={{
                    flex: 1,
                    padding: 13,
                    borderRadius: 12,
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    border: '0.5px solid rgba(255,255,255,0.18)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  onClick={() => goToStep(3)}
                  style={{
                    flex: 1,
                    padding: 13,
                    borderRadius: 12,
                    background: C.green,
                    color: '#0a0a0f',
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Siguiente →
                </button>
              </div>
            </>
          ) : null}

          {/* Step 3 — Servicios */}
          {step === 3 ? (
            <>
              <SLabel>Servicios</SLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {ROLE_DEFS.map(r => {
                  const selected = roles.includes(r.key);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => toggleRole(r.key)}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `0.5px solid ${selected ? 'rgba(29,158,117,0.55)' : 'rgba(255,255,255,0.10)'}`,
                        background: selected ? 'rgba(29,158,117,0.08)' : 'rgba(255,255,255,0.03)',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                      }}
                    >
                      <div style={{ width: 30, height: 30, borderRadius: 10, background: selected ? 'rgba(29,158,117,0.16)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <Icon name={r.icon} size={14} color={selected ? C.green : 'rgba(255,255,255,0.45)'} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: selected ? '#fff' : 'rgba(255,255,255,0.75)' }}>
                          {r.emoji ? `${r.emoji} ` : ''}
                          {r.label}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 3, lineHeight: 1.35 }}>{r.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {step3Errs.roles ? <div style={{ marginTop: -6, marginBottom: 10, fontSize: 10, color: C.danger }}>{step3Errs.roles}</div> : null}

              {canShowSchedulePicker ? (
                <>
                  <SLabel>Horario operativo</SLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                    {PARTNER_SCHEDULE_OPTIONS.map(opt => {
                      const selected = scheduleType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setScheduleType(opt.value)}
                          style={{
                            gridColumn: opt.value === 'special' ? '1 / -1' : undefined,
                            textAlign: 'left',
                            padding: '10px 12px',
                            borderRadius: 12,
                            border: `0.5px solid ${selected ? 'rgba(29,158,117,0.55)' : 'rgba(255,255,255,0.10)'}`,
                            background: selected ? 'rgba(29,158,117,0.08)' : 'rgba(255,255,255,0.03)',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: 10,
                            alignItems: 'flex-start',
                          }}
                        >
                          <div style={{ width: 30, height: 30, borderRadius: 10, background: selected ? 'rgba(29,158,117,0.16)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <Icon name={opt.icon} size={14} color={selected ? C.green : 'rgba(255,255,255,0.45)'} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: selected ? '#fff' : 'rgba(255,255,255,0.75)' }}>{opt.title}</div>
                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 3, lineHeight: 1.35 }}>{opt.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : null}

              {/* Legal */}
              <DCard style={{ padding: '10px 14px', marginBottom: 12, background: 'rgba(83,74,183,0.06)', borderColor: 'rgba(83,74,183,0.2)' }}>
                <p style={{ fontSize: 10, color: C.purpleL, lineHeight: 1.5 }}>
                  Al enviar la solicitud aceptas responsabilizarte de la custodia y/o los servicios seleccionados. DreamVinyl verificará tu NIF/CIF antes de activar tu cuenta.
                </p>
              </DCard>

              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} style={{ accentColor: C.green, marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>
                  He leído el dossier y acepto los términos <span style={{ color: C.gold }}>*</span>
                </span>
              </div>
              {step3Errs.acceptTerms ? <div style={{ marginTop: -6, marginBottom: 10, fontSize: 10, color: C.danger }}>{step3Errs.acceptTerms}</div> : null}

              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14 }}>
                <input type="checkbox" checked={confirmBusiness} onChange={e => setConfirmBusiness(e.target.checked)} style={{ accentColor: C.green, marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>
                  Confirmo que los datos corresponden a un negocio / proyecto real <span style={{ color: C.gold }}>*</span>
                </span>
              </div>
              {step3Errs.confirmBusiness ? <div style={{ marginTop: -10, marginBottom: 10, fontSize: 10, color: C.danger }}>{step3Errs.confirmBusiness}</div> : null}

              {submitError ? (
                <div style={{ marginBottom: 10, background: 'rgba(226,75,74,0.10)', border: '0.5px solid rgba(226,75,74,0.28)', padding: '10px 12px', borderRadius: 12, color: C.danger, fontSize: 11, lineHeight: 1.4 }}>
                  {submitError}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: 13,
                    borderRadius: 12,
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    border: '0.5px solid rgba(255,255,255,0.18)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleSubmit}
                  style={{
                    flex: 1,
                    padding: 13,
                    borderRadius: 12,
                    background: C.green,
                    color: '#0a0a0f',
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: submitting ? 'wait' : 'pointer',
                    opacity: submitting ? 0.75 : 1,
                  }}
                >
                  {submitting ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>

              <div style={{ marginTop: 8 }}>
                <BtnGhost>Guardar borrador</BtnGhost>
              </div>
            </>
          ) : null}

        </div>
      </div>
    </div>
  );
}

